import type { PostDto, VersionDto, MediaDto } from '@str/api/types';
import { type CardEdit, reframeEdit } from '@str/shared';
import { exportPicture } from './picture-render';
import { unwrap } from './api';
import { corsSource } from './cors-source';
export { centerCrop } from '@str/shared';
export type ComposerCard = VersionDto['cards'][number];

/**
 * The picture the automatic words are drawn on: the card the generator produced, framed as it
 * framed it, so the words version looks like the bare one with words. An edited card keeps its
 * own source; a card with no picture of its own falls back to the usual source.
 */
export function wordsBase(
  card: ComposerCard,
  localPhotos: ReadonlyMap<string, string> = new Map(),
): CardEdit['source'] {
  if (card.edit) return card.edit.source;
  if (card.imageUrl && /^https?:\/\//i.test(card.imageUrl))
    return { kind: 'url', url: corsSource(card.imageUrl) };
  return cardSource(card, localPhotos);
}

export function cardSource(
  card: ComposerCard,
  localPhotos: ReadonlyMap<string, string> = new Map(),
): CardEdit['source'] {
  if (card.edit) return card.edit.source;
  if (card.sourceMediaId && localPhotos.has(card.sourceMediaId))
    return mediaSource({ id: card.sourceMediaId, url: localPhotos.get(card.sourceMediaId)! });
  if (card.mediaId) return { kind: 'media', mediaId: card.mediaId };
  if (!card.imageUrl) throw new Error('This card has no picture to edit.');
  return { kind: 'url', url: new URL(card.imageUrl, window.location.origin).href };
}
export function mediaSource(media: Pick<MediaDto, 'id' | 'url'>): CardEdit['source'] {
  if (!media.url) throw new Error('This photo has no file. Choose another photo.');
  return media.url.startsWith('/api/media/')
    ? { kind: 'media', mediaId: media.id }
    : { kind: 'url', url: new URL(media.url, window.location.origin).href };
}
export function sourceUrl(source: CardEdit['source']) {
  return source.kind === 'media' ? `/api/media/${source.mediaId}/file` : source.url;
}
export function remoteImage(url: string) {
  return new URL(url, window.location.origin).origin !== window.location.origin;
}
export function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    if (remoteImage(url)) image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Could not load this picture. Try again.'));
    image.src = corsSource(url);
  });
}
/** The browser's single picture renderer. A recenter starts from source pixels, never a flat. */
export async function renderCrop(
  edit: CardEdit,
  recenter = false,
): Promise<{ blob: Blob; edit: CardEdit }> {
  const image = await loadImage(sourceUrl(edit.source));
  const renderedEdit = recenter
    ? reframeEdit(edit, edit.canvas, image.naturalWidth, image.naturalHeight)
    : edit;
  const blob = await exportPicture(renderedEdit, image);
  return { blob, edit: renderedEdit };
}
export async function storeCardAsset(postId: string, blob: Blob) {
  const form = new FormData();
  form.append('file', blob, blob.type === 'image/png' ? 'overlay.png' : 'card.jpg');
  const data = await unwrap<{ asset: { id: string; url: string } }>(
    fetch(`/api/posts/${postId}/assets`, { method: 'POST', body: form, credentials: 'include' }),
  );
  return data.asset;
}
/** Untouched AI cards are copied byte-for-byte to local assets so reordering never resamples. */
export async function persistCards(
  postId: string,
  parentId: string,
  cards: ComposerCard[],
  note: string,
): Promise<PostDto> {
  const stored = [];
  for (const card of cards) {
    let mediaId = card.mediaId;
    if (!mediaId) {
      if (!card.imageUrl) throw new Error('This card has no picture. Generate the pictures first.');
      const response = await fetch(card.imageUrl, {
        credentials: remoteImage(card.imageUrl) ? 'omit' : 'include',
      });
      if (!response.ok) throw new Error('Could not load a card. Try again.');
      mediaId = (await storeCardAsset(postId, await response.blob())).id;
    }
    stored.push({ ...card, mediaId });
  }
  return (
    await unwrap<{ post: PostDto }>(
      fetch(`/api/posts/${postId}/versions`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ parentVersionId: parentId, note, cards: stored }),
      }),
    )
  ).post;
}
